// /opt/isp_erp/frontend/src/pages/admin/clientes/components/NapCliente.jsx
import React, { useState, useEffect } from 'react';
import axios from "../../../../api/axios";
import './NapCliente.css';
import AsignarNapDialog from './AsignarNapDialog';

const NapCliente = ({ customerId }) => {
  const [napInfo, setNapInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [liberando, setLiberando] = useState(false);

  useEffect(() => {
    if (customerId) {
      cargarInfoNAP();
    }
  }, [customerId]);

  const cargarInfoNAP = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/customers/${customerId}/nap-info`);
      setNapInfo(response.data);
      setError(null);
    } catch (err) {
      console.error('Error cargando información del NAP:', err);
      if (err.response?.status === 404) {
        setNapInfo({ nap: null, mensaje: 'Cliente no tiene NAP asignado' });
      } else {
        setError('No se pudo cargar la información del NAP');
      }
    } finally {
      setLoading(false);
    }
  };

  const abrirMapa = (lat, lng) => {
    window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
  };

  const alAsignar = () => {
    setDialogoAbierto(false);
    cargarInfoNAP();
  };

  const liberarNap = async () => {
    if (!window.confirm('¿Liberar la NAP y el puerto de este cliente? El puerto quedará libre.')) return;
    setLiberando(true);
    try {
      await axios.delete(`/customers/${customerId}/nap`);
      await cargarInfoNAP();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo liberar la NAP');
    } finally {
      setLiberando(false);
    }
  };

  if (loading) {
    return (
      <div className="nap-loading">
        <div className="detalle-loading-spinner"></div>
        <p>Cargando información del NAP...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nap-error">
        <div className="nap-error-icon">⚠️</div>
        <p>{error}</p>
      </div>
    );
  }

  if (!napInfo || !napInfo.nap) {
    return (
      <div className="nap-empty">
        <div className="nap-empty-icon">📡</div>
        <h4>Sin Conexión NAP</h4>
        <p>El cliente aún no tiene asignada una caja NAP.</p>
        <p className="nap-empty-hint">La asignación normal se hace al instalar el servicio. Si hace falta, puedes asignarla aquí.</p>
        <button className="nap-mapa-btn" onClick={() => setDialogoAbierto(true)}>📡 Asignar NAP</button>
        <AsignarNapDialog open={dialogoAbierto} customerId={customerId} actual={null} onClose={() => setDialogoAbierto(false)} onHecho={alAsignar} />
      </div>
    );
  }

  const nivelSenal = napInfo.nivel_senal_rx;
  let senalClase = 'senal-default';
  let senalTexto = '';
  if (nivelSenal) {
    if (nivelSenal >= -25) {
      senalClase = 'senal-excelente';
      senalTexto = 'Excelente';
    } else if (nivelSenal >= -30) {
      senalClase = 'senal-aceptable';
      senalTexto = 'Aceptable';
    } else {
      senalClase = 'senal-bajo';
      senalTexto = 'Bajo';
    }
  }

  return (
    <div className="nap-container">
      <div className="nap-header">
        <div className="nap-header-icon">📡</div>
        <h3>Conexión a Caja NAP</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="nap-mapa-btn" onClick={() => setDialogoAbierto(true)}>🔁 Cambiar NAP</button>
          <button className="nap-mapa-btn" onClick={liberarNap} disabled={liberando}>{liberando ? 'Liberando...' : '⏏️ Liberar NAP'}</button>
        </div>
      </div>

      <div className="nap-info-grid">
        <div className="nap-info-item">
          <label>Código NAP</label>
          <span className="nap-value nap-codigo">{napInfo.nap.codigo}</span>
        </div>

        <div className="nap-info-item">
          <label>Nombre NAP</label>
          <span className="nap-value">{napInfo.nap.nombre}</span>
        </div>

        <div className="nap-info-item">
          <label>Puerto Asignado</label>
          <span className="nap-value nap-puerto">Puerto {napInfo.puerto_asignado}</span>
        </div>

        <div className="nap-info-item">
          <label>Nivel de Señal RX</label>
          <div className="nap-senal">
            <span className={`nap-senal-indicador ${senalClase}`}></span>
            <span className="nap-value">
              {nivelSenal ? `${nivelSenal} dBm` : 'No registrado'}
            </span>
            {nivelSenal && (
              <span className={`nap-senal-texto ${senalClase}`}>{senalTexto}</span>
            )}
          </div>
        </div>

        <div className="nap-info-item">
          <label>Estado NAP</label>
          <span className={`nap-estado nap-estado-${napInfo.nap.estado?.toLowerCase()}`}>
            {napInfo.nap.estado === 'ACTIVO' && '🟢 '}
            {napInfo.nap.estado === 'LLENO' && '🔴 '}
            {napInfo.nap.estado === 'MANTENIMIENTO' && '🟡 '}
            {napInfo.nap.estado}
          </span>
        </div>

        <div className="nap-info-item">
          <label>Ocupación NAP</label>
          <div className="nap-ocupacion">
            <div className="nap-ocupacion-bar">
              <div 
                className="nap-ocupacion-fill" 
                style={{ width: `${napInfo.nap.porcentaje_ocupacion}%` }}
              ></div>
            </div>
            <span className="nap-value">{napInfo.nap.porcentaje_ocupacion}%</span>
          </div>
        </div>
      </div>

      <div className="nap-direccion">
        <label>📍 Dirección del NAP</label>
        <p>{napInfo.nap.direccion}</p>
        {napInfo.nap.latitud && napInfo.nap.longitud && (
          <button 
            className="nap-mapa-btn"
            onClick={() => abrirMapa(napInfo.nap.latitud, napInfo.nap.longitud)}
          >
            🗺️ Ver en Google Maps
          </button>
        )}
      </div>

      {napInfo.fecha_asignacion && (
        <div className="nap-fecha">
          <label>📅 Fecha de Asignación</label>
          <p>{new Date(napInfo.fecha_asignacion).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</p>
        </div>
      )}
      <AsignarNapDialog
        open={dialogoAbierto}
        customerId={customerId}
        actual={{ codigo: napInfo.nap.codigo, puerto: napInfo.puerto_asignado }}
        onClose={() => setDialogoAbierto(false)}
        onHecho={alAsignar}
      />
    </div>
  );
};

export default NapCliente;
